import React, { useEffect, useState } from 'react';
import { Drawer, Form, Col, Row } from 'antd';
import PropTypes from 'prop-types';
import InputComponent from '@/components/Input';
import ButtonComponent from '@/components/Button';
import { ReactComponent as SaveIcon } from '@/assets/images/icons/save_icon.svg';
import { useTranslation } from 'react-i18next';
import { validateEmail, validatePhoneNumber } from '@/utils/validate';
import InputPhone from '@/components/InputPhone';
import { ProfileService } from '@/services';
import SelectFetching from '@/components/SelectFetching';
import { parsePhoneNumberFromString } from 'libphonenumber-js/max';
import { toLower } from 'lodash';

import './index.scss';

function ModalInformationComponent({ open, onClose, formData, onFinish }) {
  const [countryCode, setCountryCode] = useState('66');
  const [country, setCountry] = useState();
  const [wardData, setWardData] = useState([]);
  const [initialValuesForm, setInitialValuesForm] = useState({});
  const [loadingBtnSubmit, setLoadingBtnSubmit] = useState(false);
  const { t: translation } = useTranslation();

  const onChangeCountryCode = (code) => {
    setCountryCode(code);
  };

  const [formInstance] = Form.useForm();

  const [triggerFetching, setTriggerFetching] = useState('');
  const [billingCountry, setBillingCountry] = useState({});
  const fetchCountry = async (name) => {
    const { success, data } = await ProfileService.getCountry({ name });
    if (success) {
      const { results } = data;
      return results.map((o) => ({
        label: o.name,
        value: o.id,
      }));
    }
    return [];
  };

  const [billingProvince, setBillingProvince] = useState({});
  const fetchProvinces = async (name) => {
    const { success, data } = await ProfileService.getProvince({
      name,
      countryId: billingCountry.value,
    });
    if (success) {
      const { results } = data;
      return results.map((o) => ({
        label: o.name,
        value: o.id,
      }));
    }
    return [];
  };

  const [billingDistrict, setBillingDistrict] = useState({});
  const fetchDistrict = async (name) => {
    const { success, data } = await ProfileService.getDistrict({
      name,
      provinceId: billingProvince.value,
    });
    if (success) {
      const { results } = data;
      return results.map((o) => ({
        label: o.name,
        value: o.id,
      }));
    }
    return [];
  };

  const [billingSubDistrict, setBillingSubDistrict] = useState({});
  const fetchSubDistrict = async (name) => {
    const { success, data } = await ProfileService.getWard({
      name,
      districtId: billingDistrict.value,
    });
    if (success) {
      const { results } = data;
      setWardData(results);
      return results.map((o) => ({
        label: o.name,
        value: o.id,
      }));
    }
    return [];
  };

  const triggerFetchData = (field) => {
    setTriggerFetching(field);

    setTimeout(() => {
      setTriggerFetching('');
    }, 50);
  };

  useEffect(() => {
    if (open) {
      triggerFetchData(['country', 'city', 'district', 'sub_district']);
    }
  }, [open]);

  const isFetchingField = (field) => triggerFetching.includes(field);

  const handleOnChangeSubDistrict = (newValue) => {
    setBillingSubDistrict({
      label: newValue.label,
      value: newValue.value,
    });
    const zipcode = wardData.find(
      (item) => item.id === newValue.value,
    )?.zipcode;

    formInstance.setFieldValue('defaultBillingZipcode', zipcode);
  };

  useEffect(() => {
    const parsedPhone = parsePhoneNumberFromString(
      `+${formData.defaultBillingPhone}`,
    );
    let formValues = { ...formData };
    if (parsedPhone && parsedPhone.isValid()) {
      setCountry(toLower(parsedPhone.country));
      setCountryCode(parsedPhone.countryCallingCode);
      formValues = {
        ...formValues,
        defaultBillingPhone: parsedPhone.nationalNumber,
      };
    }

    setInitialValuesForm(formValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  const handleOnFinish = async (values) => {
    setLoadingBtnSubmit(true);
    const payload = {
      defaultBillingFirstName: values.defaultBillingFirstName,
      defaultBillingLastName: values.defaultBillingLastName,
      defaultBillingEmail: values.defaultBillingEmail,
      defaultBillingTaxNumber: values.defaultBillingTaxNumber,
      defaultBillingPhone: `${countryCode}${values.defaultBillingPhone}`,
      defaultBillingCountryId: values.defaultBillingCountry.value,
      defaultBillingDistrictId: values.defaultBillingDistrict.value,
      defaultBillingProvinceId: values.defaultBillingProvince.value,
      defaultBillingWardId: values.defaultBillingSubDistrict.value,
      defaultBillingZipcode: values.defaultBillingZipcode,
      defaultBillingAddress: values.defaultBillingAddress,
    };

    const { success } = await onFinish(payload);
    if (success) {
      onClose();
    }
    setLoadingBtnSubmit(false);
  };

  return (
    <Drawer
      title={translation('profile_page.drawer.billing_infomation')}
      className="drawer"
      placement="right"
      onClose={onClose}
      open={open}
      width={573}
      destroyOnClose
      keyboard={false}
      maskClosable={false}
    >
      <Form
        form={formInstance}
        className="billingInfo-form"
        layout="vertical"
        onFinish={handleOnFinish}
        autoComplete="off"
        initialValues={initialValuesForm}
      >
        <Row gutter={[16, 24]}>
          <Col lg={{ span: 12 }} span={24}>
            <Form.Item
              name="defaultBillingFirstName"
              rules={[
                {
                  required: true,
                  message: translation(
                    'sign_up.form.errors.first_name.required',
                  ),
                },
              ]}
            >
              <InputComponent
                placeholder={translation('sign_up.form.placeholder.first_name')}
                label={translation('sign_up.form.fields.first_name')}
              />
            </Form.Item>
          </Col>
          <Col lg={{ span: 12 }} span={24}>
            <Form.Item
              name="defaultBillingLastName"
              rules={[
                {
                  required: true,
                  message: translation(
                    'sign_up.form.errors.last_name.required',
                  ),
                },
              ]}
            >
              <InputComponent
                placeholder={translation('sign_up.form.placeholder.last_name')}
                label={translation('sign_up.form.fields.last_name')}
              />
            </Form.Item>
          </Col>
          <Col lg={{ span: 12 }} span={24}>
            <Form.Item
              name="defaultBillingEmail"
              rules={[
                {
                  required: true,
                  message: translation('login.form.errors.email.required'),
                },
                () => ({
                  validator(_, value) {
                    if (value && !validateEmail(value)) {
                      return Promise.reject(
                        new Error(
                          translation('login.form.errors.email.not_valid'),
                        ),
                      );
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <InputComponent
                placeholder={translation('login.form.placeholder.email')}
                label={translation('login.form.fields.email')}
              />
            </Form.Item>
          </Col>
          <Col lg={{ span: 12 }} span={24}>
            <Form.Item
              name="defaultBillingPhone"
              rules={[
                {
                  required: true,
                  message: translation(
                    'sign_up.form.errors.phone_number.required',
                  ),
                },
                () => ({
                  validator(_, value) {
                    if (value && !validatePhoneNumber(countryCode, value)) {
                      return Promise.reject(
                        new Error(
                          translation(
                            'sign_up.form.errors.phone_number.invalid',
                          ),
                        ),
                      );
                    }

                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <InputPhone
                onChangeCountryCode={onChangeCountryCode}
                placeholder={translation(
                  'sign_up.form.placeholder.phone_number',
                )}
                label={translation('sign_up.form.fields.phone_number')}
                country={country}
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              name="defaultBillingTaxNumber"
              rules={[
                {
                  required: true,
                  message: translation(
                    'customer_info.form.errors.tax_number.required',
                  ),
                },
                () => ({
                  validator(_, value) {
                    if (value && value.length !== 13) {
                      return Promise.reject(
                        new Error(
                          translation(
                            'customer_info.form.errors.tax_number.not_valid',
                          ),
                        ),
                      );
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <InputComponent
                type="number"
                placeholder={translation(
                  'customer_info.form.placeholder.tax_number',
                )}
                label={translation('customer_info.form.fields.tax_number')}
                info={translation('customer_info.form.info.tax_number')}
              />
            </Form.Item>
          </Col>
          <Col lg={{ span: 12 }} span={24}>
            <Form.Item
              name="defaultBillingCountry"
              rules={[
                {
                  required: true,
                  message: translation(
                    'customer_info.form.errors.country.required',
                  ),
                },
              ]}
            >
              <SelectFetching
                isDefault
                form={formInstance}
                triggerFetching={isFetchingField('country')}
                label={translation('customer_info.form.fields.country')}
                placeholder={translation(
                  'customer_info.form.placeholder.country',
                )}
                fetchOptions={fetchCountry}
                value={billingCountry}
                onChange={(newValue) => {
                  setBillingCountry({
                    label: newValue.label,
                    value: newValue.value,
                  });
                  triggerFetchData('city');
                }}
              />
            </Form.Item>
          </Col>
          <Col lg={{ span: 12 }} span={24}>
            <Form.Item
              name="defaultBillingProvince"
              rules={[
                {
                  required: true,
                  message: translation(
                    'customer_info.form.errors.city.required',
                  ),
                },
              ]}
            >
              <SelectFetching
                triggerFetching={isFetchingField('city')}
                label={translation('customer_info.form.fields.city')}
                placeholder={translation('customer_info.form.placeholder.city')}
                fetchOptions={fetchProvinces}
                value={billingProvince}
                onChange={(newValue) => {
                  setBillingProvince({
                    label: newValue.label,
                    value: newValue.value,
                  });
                  triggerFetchData('district');
                }}
              />
            </Form.Item>
          </Col>
          <Col lg={{ span: 12 }} span={24}>
            <Form.Item
              name="defaultBillingDistrict"
              rules={[
                {
                  required: true,
                  message: translation(
                    'customer_info.form.errors.district.required',
                  ),
                },
              ]}
            >
              <SelectFetching
                triggerFetching={isFetchingField('district')}
                label={translation('customer_info.form.fields.district')}
                placeholder={translation(
                  'customer_info.form.placeholder.district',
                )}
                fetchOptions={fetchDistrict}
                value={billingDistrict}
                onChange={(newValue) => {
                  setBillingDistrict({
                    label: newValue.label,
                    value: newValue.value,
                  });
                  triggerFetchData('sub_district');
                }}
              />
            </Form.Item>
          </Col>
          <Col lg={{ span: 12 }} span={24}>
            <Form.Item
              name="defaultBillingSubDistrict"
              rules={[
                {
                  required: false,
                },
              ]}
            >
              <SelectFetching
                triggerFetching={isFetchingField('sub_district')}
                label={translation('customer_info.form.fields.sub_district')}
                placeholder={translation(
                  'customer_info.form.placeholder.sub_district',
                )}
                fetchOptions={fetchSubDistrict}
                value={billingSubDistrict}
                onChange={handleOnChangeSubDistrict}
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              name="defaultBillingZipcode"
              rules={[
                {
                  required: true,
                  message: translation(
                    'customer_info.form.errors.zip_code.required',
                  ),
                },
              ]}
            >
              <InputComponent
                label={translation('customer_info.form.fields.zip_code')}
                placeholder={translation(
                  'customer_info.form.placeholder.zip_code',
                )}
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              name="defaultBillingAddress"
              rules={[
                {
                  required: false,
                },
              ]}
            >
              <InputComponent
                label={translation('customer_info.form.fields.address')}
                placeholder={translation(
                  'customer_info.form.placeholder.address',
                )}
              />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item
          wrapperCol={{
            offset: 8,
          }}
        >
          <ButtonComponent
            onClick={onClose}
            className="btn-exit"
            disabled={loadingBtnSubmit}
          >
            {translation('profile_page.btn_discard')}
          </ButtonComponent>
          <ButtonComponent
            type="primary"
            htmlType="submit"
            className="btn-saveChanges"
            icon={<SaveIcon />}
            disabled={loadingBtnSubmit}
            loading={loadingBtnSubmit}
          >
            {translation('profile_page.btn_save')}
          </ButtonComponent>
        </Form.Item>
      </Form>
    </Drawer>
  );
}

ModalInformationComponent.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onFinish: PropTypes.func,
  formData: PropTypes.any,
};

export default ModalInformationComponent;
